import { GQL } from '@aline/graphql';
import { omit } from 'ramda';
import type { NodeEntry } from 'slate';

import { createCheckboxElement } from './checkbox';
import { createDefaultText } from './text';
import { CHECKBOX_UNCHECKED, isCheckboxCheckedText } from '../../constants';
import { Queries } from '../internal';
import { inputFieldType } from '../types';
import type {
  AlineText,
  CheckboxElement,
  CheckboxOptionFieldElement,
  TextProps,
  ToggleCheckboxEdit,
} from '../types';
import { isToggleCheckboxEdit } from '../types/type-guards';

export interface CreateCheckboxOptionArgs {
  checkboxOptionProps: Partial<CheckboxOptionFieldElement> &
    Pick<CheckboxOptionFieldElement, 'fieldId' | 'id'>;
  checkboxProps?: Partial<CheckboxElement>;
  isChecked?: boolean;
  text?: string | null;
  textProps?: Partial<TextProps>;
}

export function normalizeCheckboxOptionLabelText(text: string | null | undefined): string {
  if (!text) return '';
  return text.startsWith(' ') ? text : ` ${text}`;
}

export interface CheckboxInfo {
  checkboxEntry: NodeEntry<CheckboxElement> | undefined;
  isChecked: boolean;
  labelText: string;
  labelTextEntries: NodeEntry<AlineText>[];
}

export function getToggleCheckboxEdit(checkbox: CheckboxElement): ToggleCheckboxEdit | undefined {
  return checkbox.suggestedEdits?.find(isToggleCheckboxEdit);
}

export function getEffectiveCheckboxStateText(checkbox: CheckboxElement): string {
  return (
    getToggleCheckboxEdit(checkbox)?.value.to ?? checkbox.children.at(0)?.text ?? CHECKBOX_UNCHECKED
  );
}

export function getCheckboxCheckedState(
  checkboxEntry: NodeEntry<CheckboxElement> | undefined
): boolean {
  if (!checkboxEntry) return false;
  return isCheckboxCheckedText(getEffectiveCheckboxStateText(checkboxEntry[0]));
}

export function getCheckboxInfo(optionEntry: NodeEntry<CheckboxOptionFieldElement>): CheckboxInfo {
  const { checkboxEntry, labelTextEntries } = Queries.getCheckboxFieldElements(optionEntry);

  return {
    checkboxEntry,
    isChecked: getCheckboxCheckedState(checkboxEntry),
    labelText: labelTextEntries.map(([textNode]) => textNode.text).join(''),
    labelTextEntries,
  };
}

export function createCheckboxOptionFieldElement(
  args: CreateCheckboxOptionArgs
): CheckboxOptionFieldElement {
  const {
    checkboxOptionProps: { fieldId, id, ...defaultCheckboxOptionProps },
    checkboxProps,
    isChecked,
    text,
    textProps,
  } = args;
  let optionText = text;
  if (!text && defaultCheckboxOptionProps?.children) {
    optionText = normalizeCheckboxOptionLabelText(
      getCheckboxInfo([
        { children: defaultCheckboxOptionProps.children } as CheckboxOptionFieldElement,
        [],
      ]).labelText
    );
  } else if (text) {
    optionText = normalizeCheckboxOptionLabelText(text);
  }

  const optionsTextNodes = optionText ? [createDefaultText(textProps, optionText)] : [];

  return {
    children: [
      createCheckboxElement({
        checkboxProps: checkboxProps || { optionId: id },
        isChecked,
        textProps,
      }),
      ...optionsTextNodes,
    ],
    fieldId,
    id,
    suggestedEdits: [],
    type: inputFieldType,
    variant: GQL.FieldVariant.Checkbox,
    ...omit(['children'], defaultCheckboxOptionProps),
  };
}
